import axios from "axios";
import { getApiBaseUrl } from "./apiBase";

// Create axios instance with base configuration using centralized API config
const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes to accommodate duplicate replacement processing
});

// Track if server is warming up to show appropriate loading messages
let serverWarmingUp = false;

// Store auth instance for setting token
let auth0Instance = null;

// Function to set the auth0 instance
export const setAuth0Instance = (auth0) => {
  auth0Instance = auth0;
};

// Request interceptor to add JWT token
api.interceptors.request.use(
  async (config) => {
    // Only add token for protected routes
    const protectedPaths = [
      "/api/dashboard",
      "/api/candidates/search-with-criteria",
      "/api/saved-searches",
      "/api/agents",
      "/api/ai",
      "/api/settings",
      "/api/user",
      "/api/billing",
      "/api/person-enrich",
      "/api/leads",
      "/api/engagement",
      "/api/quick-lookup",
      "/api/email",
      "/api/org",
    ];

    // Exclude public endpoints from auth requirements
    const publicPaths = [
      "/api/ai/lookups/",
    ];

    const isPublicRoute = publicPaths.some((path) =>
      config.url?.startsWith(path)
    );

    const isProtectedRoute =
      !isPublicRoute &&
      protectedPaths.some((path) => config.url?.startsWith(path));


    if (isProtectedRoute && auth0Instance) {
      try {
        // Check if Auth0 is ready before trying to get token
        if (auth0Instance.isAuthenticated && !auth0Instance.isLoading) {
          // Add timeout to prevent hanging
          const tokenPromise = auth0Instance.getAccessTokenSilently();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Token acquisition timeout')), 5000)
          );
          
          const token = await Promise.race([tokenPromise, timeoutPromise]);
          config.headers.Authorization = `Bearer ${token}`;

          // Pass Auth0 profile info so the backend can sync real emails/names
          if (auth0Instance.user?.email) {
            config.headers["X-Auth-Email"] = auth0Instance.user.email;
          }
          if (auth0Instance.user?.name) {
            config.headers["X-Auth-Name"] = auth0Instance.user.name;
          }
        }
      } catch (error) {
        // Log but don't block - let request proceed without token
        console.warn("Token acquisition skipped:", error.message || error);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and server spin-up
api.interceptors.response.use(
  (response) => {
    // Server responded successfully - mark as not warming up
    if (serverWarmingUp) {
      console.log('✅ Server connection restored');
      serverWarmingUp = false;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Log network errors for debugging (minimal)
    if (error.message === 'Network Error') {
      console.warn('Network error - check if backend is running');
    }

    // Handle server spin-up (Render free tier cold starts)
    // ECONNREFUSED, ETIMEDOUT, or 502/503/504 errors indicate server is starting
    if (
      originalRequest &&
      !originalRequest._retryCount &&
      (error.code === 'ECONNREFUSED' || 
       error.code === 'ETIMEDOUT' || 
       error.code === 'ECONNABORTED' ||
       error.message === 'Network Error' ||
       [502, 503, 504].includes(error.response?.status))
    ) {
      originalRequest._retryCount = 0;
    }

    // Retry logic for server spin-up (max 3 retries with exponential backoff)
    if (
      originalRequest &&
      originalRequest._retryCount !== undefined && 
      originalRequest._retryCount < 3 &&
      (error.code === 'ECONNREFUSED' || 
       error.code === 'ETIMEDOUT' ||
       error.code === 'ECONNABORTED' ||
       error.message === 'Network Error' ||
       [502, 503, 504].includes(error.response?.status))
    ) {
      originalRequest._retryCount++;
      const retryDelay = Math.min(1000 * Math.pow(2, originalRequest._retryCount), 10000); // Max 10s
      
      if (!serverWarmingUp) {
        serverWarmingUp = true;
        console.log(`🔄 Server appears to be starting up, retrying in ${retryDelay}ms... (attempt ${originalRequest._retryCount}/3)`);
      }

      await new Promise(resolve => setTimeout(resolve, retryDelay));
      return api(originalRequest);
    }

    // Handle authentication errors
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (auth0Instance && auth0Instance.isAuthenticated) {
        try {
          // Try to get a fresh token
          const token = await auth0Instance.getAccessTokenSilently({
            ignoreCache: true,
          });
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        } catch (tokenError) {
          console.warn("Token refresh failed:", tokenError.message);
          // Don't redirect - let the error propagate
        }
      }
    }

    return Promise.reject(error);
  }
);

export default api;
